import { ContextData, ContextFilterItem, MultiContextData, ReportContext } from "@reporting/rp-types/analytics";
import { utils } from "@omegagrid/core";
import rp from '@reporting/rp-app-host';
import { ModuleCode } from "@reporting/rp-types/accounting";
import { DimensionType, anonimizeContext, cloneContext, copyItemUids, ensureItemUids, getDimensionType, getIndicatorKey, parseIndicatorKey, isMultiContext, mergeCommonFilters, stripItemUids } from "../common";
import { ModifyContextParams } from "../types";

export class ContextBroker {

	private _hashMap: Map<string, ReportContext> = new Map();
	private _idMap: Map<number, ReportContext> = new Map();
	public currentContext: ReportContext;

	// async createContextHash(context: ReportContext) {
	// 	const str = [(context.accounting || 0).toString(), JSON.stringify(context.context)].join('.');
	// 	const enc = new TextEncoder();
	// 	const hash = await crypto.subtle.digest('SHA-1', enc.encode(str));
	// 	return Array.from(new Uint8Array(hash)).map(v => v.toString(16).padStart(2, '0')).join('');
	// }

	async getLastContext(module: ModuleCode) {
		const context = [...this._idMap.values()].find(c => c.module == module);
		return context ?? this.getContext({ module });
	}

	/**
	 * Transform context to group by or transaction detail
	 * @param context
	 * @param changes
	 * @returns
	 */
	modifyContext(context: ReportContext, changes: ModifyContextParams) {
		context = anonimizeContext(context);

		const contextData = context.context as ContextData;
		const filterDims = new Set(Object.keys(changes.filters || {}));
		// a concrete period (PM, PW, PX) replaces every other concrete period filter; the common
		// period (PC) is a plain dimension - it only replaces the PC filter and keeps the rest
		const isConcretePeriod = (dim: string) => getDimensionType(dim as DimensionType) === 'P' && dim.toUpperCase() != 'PC';
		const periodDimension = [...filterDims].find(isConcretePeriod);

		// update existing filters
		contextData.filters ??= [];
		for (let i = 0; i < contextData.filters.length; i++) {
			const filter = contextData.filters[i];
			if (periodDimension && isConcretePeriod(filter.dim)) {
				const value = changes.filters[periodDimension];
				contextData.filters[i] = {dim: periodDimension, items: [[value, 0, 1]]};
				if (/^\d+$/.test(value)) {
					// change dynamic period to static
					contextData.filters[i].dim = value.length == 7 ? 'PW' : 'PM';
				}

				filterDims.delete(periodDimension);
			} else if (filterDims.has(filter.dim)) {
				contextData.filters[i].items = [[changes.filters[filter.dim], 0, 1]];
				filterDims.delete(filter.dim);
			}
		}

		// add remaining filters
		filterDims.forEach(dim => {
			contextData.filters.push({dim: dim, items: [[changes.filters[dim], 0, 1]]});
		});

		if (changes.view != null) {
			// switch view (e.g. extension resource detail)
			contextData.view = changes.view;
			if (changes.parameters) contextData.parameters = {...contextData.parameters, ...changes.parameters};
			if (changes.indicator != null && contextData.indicators?.[changes.indicator]) {
				// remember the clicked indicator (mapped to the 'indicator' extension parameter)
				contextData.indicators.forEach(ind => ind.active = false);
				contextData.indicators[changes.indicator].active = true;
			}
		} else if (changes.dim != null) {
			// group by
			contextData.y_axis = [{dim: changes.dim}];
			const filter = contextData.filters.find(f => f.dim === changes.dim);
			if (filter) {
				const newItems: ContextFilterItem[] = [];
				(filter.items as ContextFilterItem[] ?? []).forEach(item => {
					newItems.push(...(new Array(8)).fill(null).map<ContextFilterItem>((_, i) => [item[0], i, 1]));
				});
				filter.items = newItems;
			} else {
				contextData.filters.push({
					dim: changes.dim,
					items: (new Array(8)).fill(null).map<ContextFilterItem>((_, i) => [null, i, 1]),
				});
			}
		} else {
			// transaction detail
			contextData.view = 'transactions';
			contextData.y_axis = [];
			contextData.x_axis = [];
			if (changes.indicator != null) {
				contextData.indicators.forEach(ind => ind.active = false);
				let indicator = contextData.indicators[changes.indicator];
				if (!indicator) indicator = contextData.indicators[0];
				if (indicator) indicator.active = true;
			}
		}

		return context;
	}

	invalidateContext(context: Partial<ReportContext>) {
		if (context.hash && this._hashMap.has(context.hash)) {
			this._hashMap.delete(context.hash);
		}

		if (context.id_context && this._idMap.has(context.id_context)) {
			this._idMap.delete(context.id_context);
		}
	}

	async getContext(context: Partial<ReportContext>, options?: {
		save?: boolean,
		itemIndex?: number
	}) : Promise<ReportContext> {
		if (context.id_context == -1 && this.currentContext) return this.currentContext;
		
		let module = context.module?.toUpperCase() as ModuleCode;
		let contextData = context.context as ContextData | MultiContextData;
		if (!module && contextData) module = context.context?.module?.toUpperCase() as ModuleCode;
		if (contextData && contextData.module !== module) {
			// never mutate the caller's context data - it may be a frozen store snapshot
			contextData = cloneContext(contextData);
			contextData.module = module;
		}

		if (context.hash && this._hashMap.has(context.hash)) {
			context = this._hashMap.get(context.hash);
		} else if (contextData) {
			// runtime item uids must not reach the server (see common.ts)
			const payload = stripItemUids(cloneContext({context: contextData} as ReportContext));
			context = await rp.ms('analytics').post<ReportContext>(
				`context/${context.id_context || 0}`,
				payload,
				{copy: 1, save: options?.save === false ? 0 : 1}
			);
		} else if (context.id_context > 0 && this._idMap.has(context.id_context)) {
			context = this._idMap.get(context.id_context);
		} else {
			context = await rp.ms('analytics').get<ReportContext>(`context/${context.id_context || 0}`, {m: module});
		}

		this._idMap.set(context.id_context, context);
		this._hashMap.set(context.hash, context);

		// stable runtime identity of dashboard items (component ids, subcontext resolution)
		if (isMultiContext(context)) ensureItemUids(context.context as MultiContextData);

		if (isMultiContext(context) && options?.itemIndex != null) {
			context = await this.getSubcontext(context, options.itemIndex);
		}

		return context;
	}

	async getSubcontext(context: Partial<ReportContext>, index: number) {
		let contextData = context.context;
		const item = (contextData as MultiContextData)?.items[index];
		if (!item) return null;

		let subContextData: ContextData|MultiContextData;
		if (item.id_context) {
			const subContext = await this.getContext({id_context: item.id_context});
			subContextData = subContext?.context;
		} else {
			subContextData = item.context;
		}

		// always work on a copy - subContextData points into the store draft
		// (item.context) or a cached subcontext; neither the merged parent
		// filters nor later mutations by the item visualization (sorting,
		// collapse states) may leak into the stored definition
		subContextData = cloneContext(subContextData);

		// merge parent filters
		if (contextData.filters?.length) {
			subContextData.filters = mergeCommonFilters(contextData.filters, subContextData.filters, item.locked_dimensions);
		}

		return {
			name: item.title || context.name,
			id_context: 0, //item.id_context || 0,
			module: subContextData?.module,
			context: subContextData as ContextData | MultiContextData,
		};
	}

	async save(context: ReportContext, copy = false) {
		// runtime item uids must not be persisted; carry them over to the saved
		// context afterwards so component ids stay stable across saves
		const payload = stripItemUids(cloneContext(context));
		let saved: ReportContext;
		if (!context.id_context || copy) {
			saved = await rp.ms('analytics').post<ReportContext>('context', payload, {copy: copy ? 1 : 0});
		} else {
			saved = await rp.ms('analytics').put<ReportContext>(`context/${context.id_context}`, payload);
		}
		copyItemUids(context, saved);
		this._hashMap.set(saved.hash, saved);
		this._idMap.set(saved.id_context, saved);
		return saved;
	}

	async saveAs(context: ReportContext) {
		delete context.id_context;
		delete context.idz;
		return this.save(context, false);
	}

}
